import { css } from '@emotion/react';
import { Formik } from 'formik';
import { useRef } from 'react';

import { ApiOrgProperty } from '@kaiizen/kai-pro-api';

import { NOTES_CHARACTER_LIMIT } from '../../noteConstants';
import { useToast } from '../../State';
import { LAYOUT2, TYPOGRAPHY2 } from '../../Styles';
import {
  Button,
  FormFieldsRequirementNote,
  FormikLabeledCalendarInput,
  FormikLabeledInput,
  FormikLabeledMultiSelect,
  FormikLabeledSelect,
  StyledForm,
} from '../../Widgets';
import { type NewPropertyProjectValues, useCreatePropertyProject } from './useCreatePropertyProject';

export const SUCCESS_STYLES = css`
  label: SUCCESS_STYLES;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin: 20px auto 0 auto;
  max-width: 67%;
  text-align: center;
`;

const FORM_ROW_STYLE = css`
  label: FORM_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
`;

const MISSING_PIECE_STYLE_CONTAINER = css`
  label: MISSING_PIECE_STYLE_CONTAINER;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT2.spacing2}px;
`;

const MISSING_PIECE_STYLE = css`
  label: MISSING_PIECE_STYLE;
  color: var(--text2);
  ${TYPOGRAPHY2.small}
`;

export const CREATE_PROJECT_LABEL = 'Create Project';
export const CREATING_PROJECT_LABEL = 'Creating Project…';

export const ProjectPanel = ({ property, projectMutators }: {
  property: ApiOrgProperty;
  projectMutators: any;
}) => {
  const { showToast, showErrorToast } = useToast();
  // Formik doesn't refuse a second submit while the first is still running; this does.
  const createInFlightRef = useRef(false);
  const {
    status,
    blocker,
    projectTypeOptions,
    templateOptions,
    userOptions,
    managerOptions,
    initialValues,
    validate,
    createProject,
  } = useCreatePropertyProject(property, projectMutators);

  if (status === 'loading') return null;
  if (blocker) {
    return (
      <div css={blocker.actions.length > 1 ? MISSING_PIECE_STYLE_CONTAINER : undefined}>
        <div css={MISSING_PIECE_STYLE}>{blocker.message}</div>
        {blocker.actions.map(action => <Button key={action.label} onClick={action.onClick}>{action.label}</Button>)}
      </div>
    );
  }

  return (
    <Formik<NewPropertyProjectValues>
      enableReinitialize
      initialValues={initialValues}
      validate={validate}
      onSubmit={async values => {
        if (createInFlightRef.current) return;
        createInFlightRef.current = true;
        try {
          await createProject(values);
          showToast('Project created successfully', 'success');
        } catch (error) {
          showErrorToast(error, 'create a project');
        } finally {
          createInFlightRef.current = false;
        }
      }}
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
      }) => (
        <StyledForm onSubmit={handleSubmit}>
          <FormFieldsRequirementNote variant='optional' />
          <span>Create a project by entering the information below.</span>
          <div css={FORM_ROW_STYLE}>
            <FormikLabeledSelect
              name='projectType'
              label='Project Type'
              items={projectTypeOptions}
            />
            <FormikLabeledInput name='budget' label='Project Budget' type='number' unit='usd' optional />
          </div>
          <FormikLabeledInput name='name' label='Project Name' optional />
          <div css={FORM_ROW_STYLE}>
            <FormikLabeledCalendarInput name='scanDueDate' label='Scope Due Date' />
            <FormikLabeledCalendarInput name='projectDueDate' label='Project End Date' />
          </div>
          <div css={FORM_ROW_STYLE}>
            <FormikLabeledInput name='doorCode' label='Door Code' optional />
            <FormikLabeledInput name='gateCode' label='Gate Code' optional />
          </div>
          <FormikLabeledInput
            name='description'
            label='Project Dispatch Notes'
            placeholder=''
            multiline
            rows={6}
            optional
            maxLength={NOTES_CHARACTER_LIMIT}
            showCharacterCount
          />
          <FormikLabeledSelect
            name='scanTemplateId'
            label='Project Template'
            items={templateOptions}
            emptyMessage='No templates available. Create a scan template first.'
          />
          <FormikLabeledMultiSelect
            name='managerIds'
            label='Assigned Managers'
            // Any org member can manage (the server's rule), not just people linked to this market.
            items={managerOptions}
            placeholder='Select managers...'
          />
          <FormikLabeledSelect
            name='assignedUserId'
            label='Field User'
            items={userOptions}
            disabled={userOptions.length === 0}
            emptyMessage={'No users in this market. Add users to the market in Markets.'}
          />
          {
            /* Formik holds isSubmitting until the create settles, so a second click (or Enter) can't
              create the project twice while the first request is in flight. */
          }
          <Button
            disabled={isSubmitting
              || !values.projectType
              || !values.scanTemplateId
              || !values.assignedUserId
              || !values.managerIds?.length}
            type={'submit'}
          >
            {isSubmitting ? CREATING_PROJECT_LABEL : CREATE_PROJECT_LABEL}
          </Button>
        </StyledForm>
      )}
    </Formik>
  );
};
